import type { ReactNode } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { cn } from '@/shared/utils/cn';

import { AlertIcon } from './icons';

/**
 * What went wrong, announced to a screen reader.
 *
 * `role="alert"` and nothing else was copied into eight places across four features, and
 * a copy is a place where somebody forgets the role. `tone="warning"` is for the failure
 * that costs a filter or a hint, `tone="error"` for the one that costs the screen.
 *
 * `children` is the **context**, not a replacement message: "No se pudo cargar la lista
 * de buzones" in front of the `detail` the API sent. Without it the user reads a bare
 * "Error de red" and has no idea which part of the screen just stopped working. The rule
 * that the API's own `detail` is shown verbatim still holds: this only says where.
 */
export function ErrorMessage({
  error,
  tone = 'error',
  className,
  children,
}: {
  error: unknown;
  tone?: 'error' | 'warning';
  className?: string;
  children?: ReactNode;
}) {
  return (
    <p
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-[14px]',
        tone === 'error'
          ? 'bg-danger-soft text-danger border-danger/20'
          : 'bg-warn-soft text-warn border-warn/25',
        className,
      )}
    >
      <AlertIcon size={16} className="mt-0.5 shrink-0" />
      <span>
        {children ? <>{children}: </> : null}
        {errorMessage(error)}
      </span>
    </p>
  );
}
